import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Download, FileCheck, Receipt, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL, INVOICE_STATUS, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

export default function Invoices() {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState(null);
    const [uploading, setUploading] = useState(null);
    const isBuyer = user?.role !== "supplier";

    const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
    useEffect(() => { load().catch(() => {}); }, []);

    const uploadPdf = async (inv, file) => {
        if (!file) return;
        setUploading(inv.invoice_id);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/invoices/${inv.invoice_id}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(r.data.late ? "PDF yüklendi ancak süre aşıldı — admin bilgilendirildi" : "Fatura PDF'i yüklendi ve doğrulandı");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "PDF yüklenemedi");
        } finally {
            setUploading(null);
        }
    };

    const dueBadge = (inv) => {
        if (inv.pdf_uploaded) return null;
        if (inv.days_left === null || inv.days_left === undefined) return null;
        if (inv.days_left < 0) return <Badge variant="outline" className="border-red-300 bg-red-100 text-[10px] text-red-800" data-testid={`invoice-overdue-${inv.invoice_no}`}>Süre Doldu</Badge>;
        if (inv.days_left <= 1) return <Badge variant="outline" className="border-red-200 bg-red-50 text-[10px] text-red-700" data-testid={`invoice-due-soon-${inv.invoice_no}`}><Clock className="mr-1 h-3 w-3" />Son {inv.days_left} gün</Badge>;
        if (inv.days_left <= 3) return <Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800" data-testid={`invoice-due-soon-${inv.invoice_no}`}><Clock className="mr-1 h-3 w-3" />{inv.days_left} gün kaldı</Badge>;
        return <Badge variant="outline" className="text-[10px] text-muted-foreground"><Clock className="mr-1 h-3 w-3" />{inv.days_left} gün</Badge>;
    };

    const pay = async (inv) => {
        try {
            await api.post(`/invoices/${inv.invoice_id}/pay`);
            toast.success(`${inv.invoice_no} ödendi olarak işaretlendi`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        }
    };

    const confirmPayment = async (inv) => {
        try {
            await api.post(`/invoices/${inv.invoice_id}/confirm-payment`);
            toast.success(`${inv.invoice_no} — ödeme alındı olarak onaylandı`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Onaylanamadı");
        }
    };

    const issued = (invoices || []).filter((i) => i.status === "issued");
    const overdue = issued.filter((i) => i.payment_overdue);

    const vadeBadge = (inv) => {
        if (inv.status !== "issued") return null;
        if (inv.payment_days_left === null || inv.payment_days_left === undefined) return <span className="text-muted-foreground">—</span>;
        if (inv.payment_overdue) return <Badge variant="outline" className="border-red-300 bg-red-100 text-[10px] text-red-800" data-testid={`vade-overdue-${inv.invoice_no}`}>{-inv.payment_days_left} gün gecikti</Badge>;
        if (inv.payment_days_left <= 3) return <Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800" data-testid={`vade-soon-${inv.invoice_no}`}>Son {inv.payment_days_left} gün</Badge>;
        return <span className="text-muted-foreground">{inv.payment_days_left} gün</span>;
    };

    return (
        <div className="space-y-6" data-testid="invoices-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Faturalar</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{isBuyer ? "Ödeme bekleyen ve geçmiş faturalarınız" : "Kestiğiniz faturalar ve tahsilat durumu"}</p>
                </div>
                {issued.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm" data-testid="unpaid-summary">
                            <span className="font-semibold text-amber-800">{issued.length} fatura</span>
                            <span className="text-amber-700"> ödeme bekliyor — </span>
                            <span className="font-display font-extrabold text-amber-900">{fmtTL(issued.reduce((a, i) => a + i.total, 0))}</span>
                        </div>
                        {overdue.length > 0 && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm" data-testid="overdue-summary">
                                <span className="font-semibold text-red-800">{overdue.length} fatura</span>
                                <span className="text-red-700"> vadesi geçti — </span>
                                <span className="font-display font-extrabold text-red-900">{fmtTL(overdue.reduce((a, i) => a + i.total, 0))}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!invoices ? (
                <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="invoices-table">
                            <thead>
                                <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Fatura No</th>
                                    <th className="p-4">Sipariş</th>
                                    <th className="p-4">{isBuyer ? "Tedarikçi" : "Alıcı"}</th>
                                    <th className="p-4">Ara Toplam</th>
                                    <th className="p-4">KDV</th>
                                    <th className="p-4">Toplam</th>
                                    <th className="p-4">Durum</th>
                                    <th className="p-4">Tarih</th>
                                    <th className="p-4">Vade</th>
                                    <th className="p-4 text-right">PDF / İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.invoice_id} className={`border-b last:border-0 hover:bg-muted/40 ${inv.status === "issued" && inv.buyer_paid ? "bg-yellow-300 [&>td]:text-stone-900" : ""}`} data-testid={`invoice-row-${inv.invoice_no}`}>
                                        <td className="p-4 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                                        <td className="p-4"><Link to={`/panel/siparis/${inv.order_id}`} className="font-semibold text-primary hover:underline">{inv.order_no}</Link></td>
                                        <td className="p-4">{isBuyer ? inv.supplier_name : inv.buyer_company_name}</td>
                                        <td className="p-4 text-muted-foreground">{fmtTL(inv.subtotal)}</td>
                                        <td className="p-4 text-muted-foreground">{fmtTL(inv.vat)}</td>
                                        <td className="p-4 font-display font-extrabold text-terra-600">{fmtTL(inv.total)}</td>
                                        <td className="p-4">
                                            <div className="flex flex-col gap-1">
                                                <Badge variant="outline" className={`w-fit text-[10px] ${INVOICE_STATUS[inv.status]?.cls}`} data-testid={`invoice-status-${inv.invoice_no}`}>{INVOICE_STATUS[inv.status]?.label}</Badge>
                                                {inv.status === "issued" && inv.buyer_paid && (
                                                    <Badge variant="outline" className="w-fit border-amber-300 bg-amber-100 text-[10px] font-semibold text-amber-900" data-testid={`invoice-awaiting-confirm-${inv.invoice_no}`}>
                                                        {isBuyer ? "Tedarikçi Onayı Bekleniyor" : "Onayınız Bekleniyor"}
                                                    </Badge>
                                                )}
                                                {dueBadge(inv)}
                                            </div>
                                        </td>
                                        <td className="p-4 text-muted-foreground">{fmtDate(inv.issued_at)}</td>
                                        <td className="p-4 text-xs" data-testid={`vade-cell-${inv.invoice_no}`}>
                                            {inv.payment_due_at ? <div className="flex flex-col gap-1"><span className="text-muted-foreground">{fmtDate(inv.payment_due_at)}</span>{vadeBadge(inv)}</div> : <span className="text-muted-foreground">—</span>}
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex flex-wrap justify-end gap-2">
                                                {inv.pdf_uploaded ? (
                                                    <a href={`${API}/invoices/${inv.invoice_id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted" data-testid={`invoice-pdf-${inv.invoice_no}`}>
                                                        <Download className="h-3.5 w-3.5" /> PDF
                                                    </a>
                                                ) : !isBuyer && (
                                                    <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-terra-300 bg-terra-50 px-2.5 py-1.5 text-xs font-medium text-terra-700 hover:bg-terra-100" data-testid={`invoice-upload-${inv.invoice_no}`}>
                                                        <input type="file" accept=".pdf" className="hidden" disabled={uploading === inv.invoice_id} onChange={(e) => uploadPdf(inv, e.target.files[0])} data-testid={`invoice-upload-input-${inv.invoice_no}`} />
                                                        <Upload className="h-3.5 w-3.5" /> {uploading === inv.invoice_id ? "Yükleniyor..." : "PDF Yükle"}
                                                    </label>
                                                )}
                                                {inv.pdf_uploaded && !isBuyer && <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800"><FileCheck className="mr-1 h-3 w-3" />Doğrulandı</Badge>}
                                                {isBuyer && inv.status === "issued" && !inv.buyer_paid && (
                                                    <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => pay(inv)} data-testid={`invoice-pay-${inv.invoice_no}`}>
                                                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Ödendi İşaretle
                                                    </Button>
                                                )}
                                                {!isBuyer && inv.status === "issued" && inv.buyer_paid && (
                                                    <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => confirmPayment(inv)} data-testid={`invoice-confirm-${inv.invoice_no}`}>
                                                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Ödeme Alındı
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!invoices.length && (
                                    <tr><td colSpan={9} className="p-16 text-center text-sm text-muted-foreground" data-testid="invoices-empty">
                                        <Receipt className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Fatura bulunmuyor.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
